import React, { createContext, useCallback, useContext, useState } from "react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Warning, TrashSimple } from "@phosphor-icons/react";

const ConfirmContext = createContext(null);

/**
 * Merkezi silme/onay diyaloğu.
 * Kullanım:
 *   const confirm = useConfirm();
 *   const ok = await confirm({
 *     title: "Firma Sil",
 *     description: "\"ABC İnşaat\" verisini silmek istiyor musunuz?",
 *     confirmText: "Sil",
 *     destructive: true,
 *   });
 *   if (!ok) return;
 */
export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null);

  const confirm = useCallback(
    (opts) => new Promise((resolve) => setState({ ...opts, resolve })),
    []
  );

  const settle = (val) => {
    if (state?.resolve) state.resolve(val);
    setState(null);
  };

  const destructive = !!state?.destructive;

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog
        open={!!state}
        onOpenChange={(open) => { if (!open) settle(false); }}
      >
        <AlertDialogContent className="rounded-none sm:rounded-sm max-w-md" data-testid="confirm-dialog">
          <AlertDialogHeader>
            <div className="flex items-start gap-3">
              <div
                className={`w-10 h-10 shrink-0 flex items-center justify-center ${
                  destructive ? "bg-red-600" : "bg-[#1B4776]"
                }`}
              >
                {destructive ? (
                  <TrashSimple size={20} weight="duotone" className="text-white" />
                ) : (
                  <Warning size={20} weight="duotone" className="text-white" />
                )}
              </div>
              <div className="flex-1 min-w-0 text-left">
                <AlertDialogTitle className="font-heading font-bold text-lg text-slate-900">
                  {state?.title || "Onay"}
                </AlertDialogTitle>
                {state?.description && (
                  <AlertDialogDescription className="mt-1.5 text-sm text-slate-600 leading-relaxed">
                    {state.description}
                  </AlertDialogDescription>
                )}
              </div>
            </div>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-4">
            <AlertDialogCancel
              onClick={() => settle(false)}
              data-testid="confirm-cancel"
              className="rounded-none sm:rounded-sm border-slate-300"
            >
              {state?.cancelText || "Vazgeç"}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => settle(true)}
              data-testid="confirm-ok"
              className={`rounded-none sm:rounded-sm text-white ${
                destructive
                  ? "bg-red-600 hover:bg-red-700"
                  : "bg-[#1B4776] hover:bg-[#153859]"
              }`}
            >
              {state?.confirmText || (destructive ? "Sil" : "Onayla")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    // Provider yoksa bile eski davranışa düşmesin — window.confirm fallback
    return async ({ description, title }) =>
      // eslint-disable-next-line no-alert
      window.confirm(description || title || "Onaylıyor musunuz?");
  }
  return ctx;
}
